/**
 * Registers the device for APNs push when running in the native iOS app (Capacitor).
 * Runs once when user and selectedBusiness are available; sends the token to the backend.
 * Also handles notification tap: opens Messages and the conversation (customer/contact).
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { setPushDeviceToken, getLastKnownPushToken } from '../lib/pushDeviceToken';
import { deepLinkNavigate } from '../lib/deepLinkNavigation';

export function NativePushRegistration() {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const registeredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id || !selectedBusiness?.id) {
      console.log('[NativePush] No user or business, clearing ref');
      registeredRef.current = null;
      return;
    }

    let cancelled = false;
    let removeListener: (() => void) | null = null;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const registerNativePush = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.getPlatform() !== 'ios') {
          console.log('[NativePush] Not iOS, skipping');
          return;
        }

        console.log('[NativePush] Setting up for user', user.id, 'business', selectedBusiness.id);

        const { PushNotifications } = await import('@capacitor/push-notifications');

        const perm = await PushNotifications.checkPermissions();
        if (perm.receive !== 'granted') {
          const request = await PushNotifications.requestPermissions();
          if (request.receive !== 'granted') {
            console.log('[NativePush] Permission not granted');
            return;
          }
        }

        // Add listener BEFORE register() so we don't miss the token (plugin may emit immediately)
        const listener = await PushNotifications.addListener(
          'registration',
          async (event: { value: string }) => {
            const token = event?.value;
            console.log('[NativePush] registration event, token:', token ? `${token.substring(0, 12)}...` : 'null', 'cancelled:', cancelled);
            if (cancelled || !token) return;
            // Always send to backend when we get the token (needed after login; backend upserts by endpoint)
            if (registeredRef.current === token) {
              console.log('[NativePush] Same token as last time, still calling register-push-device to re-register after login');
            }
            registeredRef.current = token;

            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
              console.log('[NativePush] No session, skipping backend register');
              return;
            }

            console.log('[NativePush] Calling register-push-device');
            const { error } = await supabase.functions.invoke('register-push-device', {
              body: {
                business_id: selectedBusiness.id,
                platform: 'ios',
                device_token: token,
              },
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            });

            if (error) {
              console.warn('[NativePush] register-push-device error:', error);
              registeredRef.current = null;
              setPushDeviceToken(null);
            } else {
              console.log('[NativePush] register-push-device success');
              setPushDeviceToken(token);
            }
          }
        );
        removeListener = () => listener.remove();

        await PushNotifications.register();
        console.log('[NativePush] register() called');

        // Fallback: plugin may not re-emit "registration" after login (only fires once per app launch).
        // After a short delay, if we have a last-known token and session, re-register with the backend.
        fallbackTimeoutId = setTimeout(async () => {
          if (cancelled) return;
          const token = getLastKnownPushToken();
          if (!token) return;
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) return;
          console.log('[NativePush] Fallback: re-registering with lastKnownToken');
          const { error } = await supabase.functions.invoke('register-push-device', {
            body: { business_id: selectedBusiness.id, platform: 'ios', device_token: token },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (error) console.warn('[NativePush] Fallback register error:', error);
          else {
            console.log('[NativePush] Fallback register success');
            setPushDeviceToken(token);
          }
        }, 2500);
      } catch (e) {
        console.warn('[NativePush] setup error:', e);
      }
    };

    registerNativePush();

    return () => {
      cancelled = true;
      removeListener?.();
      if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
    };
  }, [user?.id, selectedBusiness?.id]);

  // When user taps a push notification, open the URL (e.g. /messages?customerId=... or ?contact=...)
  useEffect(() => {
    let removeAction: (() => void) | null = null;

    const setupNotificationAction = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isNativePlatform()) return;

        const { PushNotifications } = await import('@capacitor/push-notifications');
        const listener = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (event: { notification: { data?: { url?: string }; url?: string }; actionId?: string }) => {
            // APNs spreads custom data at payload root, so url can be in data.url or at notification root
            const url =
              (event.notification?.data && typeof event.notification.data === 'object' && (event.notification.data as { url?: string }).url) ||
              (event.notification as { url?: string })?.url;
            // Route in-app via React Router instead of window.location.href (a hard
            // reload blanks the native webview and breaks the post-open call flow).
            if (url && typeof url === 'string' && url.startsWith('/')) {
              deepLinkNavigate(url);
            }
          }
        );
        removeAction = () => listener.remove();
      } catch (e) {
        console.warn('[NativePush] pushNotificationActionPerformed listener:', e);
      }
    };

    setupNotificationAction();
    return () => {
      removeAction?.();
    };
  }, []);

  return null;
}
